"use client";

/**
 * Build-in-Lev handoff: stash the idea locally, then best-effort mint + push
 * the server session so the onboarding claim path works even when the visitor
 * signs up in the same tab (cookie survives) AND when they open an email
 * verification link in a different browser context (the claim token in the
 * redirect URL survives — see ADR 041, Layer 4).
 *
 * CLIENT-ONLY by design (the "use client" directive makes a server import a
 * build error): the module-level single-flight state below (`handoffChain`,
 * `latestHandoffId`) is only correct in a browser, where module scope is
 * per-tab and the functions run exclusively in click handlers. On a shared
 * serverless instance (Vercel Fluid Compute reuses instances across
 * concurrent requests) the same state would leak across REQUESTS — one
 * founder's click superseding another founder's navigation.
 *
 * This module is deliberately separate from `@/lib/onboarding/session` —
 * session.ts is `vi.mock`'d in ~10 test files that enumerate exports, and
 * adding an export there would break every one of them (CLAUDE.md testing
 * gotcha). Producers import `stashIdeaForSignup` from here; the page imports
 * from session.ts as before.
 */
import {
  buildSkipResult,
  type OnboardingResult,
} from "@/lib/onboarding/schema";
import { savePendingIdea, type OnboardingHandoffSource } from "@/lib/onboarding/session";
import { applyProgramSeed } from "@/lib/onboarding/programContext";
import { buildAuthRedirectHref, referralIdFromPath } from "@/lib/referral";
import type { ProgramSlug } from "@/constants/programs";
import { SS_ONBOARDING_CLAIM_TOKEN } from "@/lib/storage-keys";
import { currentPosthogDistinctId } from "@/lib/analytics/browserDistinctId";
import type { LandingPageSlug } from "@/lib/analytics/events";

/** The onboarding entry point that consumes a stashed/claimed idea. */
export const ONBOARDING_COMPLETE_PATH = "/onboarding/complete";

/**
 * Where a handoff surface sends the visitor after stashing: straight to
 * completion when signed in, through an auth page otherwise — carrying the
 * claim token (?oc=) in both cases so the claim survives browser-context
 * switches (ADR 041). Single owner of the redirect shape: every producer
 * surface (idea-finder, Zeitgeist, landing chat, mid-chat sign-in) calls this
 * instead of hand-rolling the URL, so a path/param change lands everywhere at
 * once. `authPath` defaults to `/sign-up` (every pre-LEV-1925 caller signs the
 * visitor up); the mid-chat header Login link is the one caller that already
 * has an account and passes `/sign-in`.
 *
 * `from` (a trusted partner slug) rides `/onboarding/complete?from=<slug>` so
 * the completion page returns an abandoning visitor to the lander they arrived
 * on. `oc` stays first so the token is never reordered behind the slug.
 */
export function buildOnboardingHandoffDestination(opts: {
  token: string | null;
  isSignedIn: boolean;
  email?: string;
  from?: string;
  authPath?: "/sign-in" | "/sign-up";
}): string {
  const params = new URLSearchParams();
  if (opts.token) params.set("oc", opts.token);
  if (opts.from) params.set("from", opts.from);
  const query = params.toString();
  const redirectUrl = query ? `${ONBOARDING_COMPLETE_PATH}?${query}` : ONBOARDING_COMPLETE_PATH;
  return opts.isSignedIn
    ? redirectUrl
    : buildAuthRedirectHref(opts.authPath ?? "/sign-up", {
        redirectUrl,
        email: opts.email,
        src: opts.from,
      });
}

/**
 * The trusted partner slug for a landing page, or undefined for the home
 * landing or an untrusted slug. `referralIdFromPath` is the single allow-list;
 * shared by every landing surface (chat handoff, header sign-in) so a slug
 * that isn't wired into `LANDING_PAGE_SLUGS` never becomes a `?from=`/`?src=`
 * value.
 */
export function resolvePartnerSlug(landingPageSlug: LandingPageSlug): string | undefined {
  return landingPageSlug !== "home" && referralIdFromPath(`/${landingPageSlug}`)
    ? landingPageSlug
    : undefined;
}

/**
 * The header's mid-chat "Sign in" href (LEV-1925): straight to
 * `/onboarding/complete` (via `/sign-in`, not `/sign-up` — the visitor already
 * has an account) so the founder's in-progress idea resumes instead of
 * getting lost. Deliberately carries NO claim token: the chat's session is
 * already tied to the browser's cookie, and this link is public-facing (it
 * renders in the DOM at all times), so a token here would leak into page
 * source. `slug` is the store's `midChatSlug` — null (no chat) is the only
 * caller-side gate; this function itself just forwards whatever partner slug
 * it's given (empty string for the home landing) as `from`/`src`.
 */
export function buildMidChatSignInHref(slug: string | null): string {
  return buildOnboardingHandoffDestination({
    token: null,
    isSignedIn: false,
    from: slug || undefined,
    authPath: "/sign-in",
  });
}

/** Time cap for the best-effort server persist (mint + push). */
const SERVER_PERSIST_TIMEOUT_MS = 2500;

export type StashIdeaForSignupResult = {
  /** The raw claim token if the mint succeeded; null otherwise. */
  token: string | null;
};

/**
 * Stash an idea for conversion after sign-up. Two phases:
 *
 * 1. **Synchronous local stash** (sessionStorage) — ALWAYS runs first, even if
 *    the network is down. This is the pending-idea fallback the completion page
 *    reads as a last resort (Layer 1).
 *
 * 2. **Best-effort server persist** (POST mint + PATCH push) — time-capped so a
 *    slow network never blocks navigation. On success, the completion page gets
 *    the authoritative server session via the claim path. On failure, the local
 *    stash (phase 1) catches it.
 *
 * Never throws — callers navigate immediately after awaiting. Concurrent
 * calls are serialized: rapid clicks (same or different idea cards) queue
 * behind one another instead of racing the mint, the PATCH, and the stashed
 * token. Last click's stash wins (sessionStorage last-write), and its PATCH
 * lands last server-side — completion builds the user's final selection.
 */
let handoffChain: Promise<unknown> = Promise.resolve();

export async function stashIdeaForSignup(payload: {
  idea: string;
  rubric: "venture" | "bootstrap";
  title?: string;
  /** Surface stamping the stash — completion telemetry names it on loss. */
  source: OnboardingHandoffSource;
  /** Seeds the program's registry values, as the program chat route does. */
  program?: ProgramSlug;
}): Promise<StashIdeaForSignupResult> {
  // Phase 1: synchronous local stash — must succeed before anything else,
  // and before queuing so the newest intent is stashed even while an earlier
  // handoff's network phase is still in flight.
  const result = buildOnboardingResultForHandoff(payload);
  savePendingIdea({
    idea: payload.idea,
    rubric: payload.rubric,
    onboardingResult: result,
    source: payload.source,
  });

  const run = handoffChain.then(() => serverPersist(result));
  // benign: an individual failure is handled inside serverPersist; the chain
  // link must never reject or every later handoff would fail with it.
  handoffChain = run.catch(() => null);
  return run;
}

/**
 * Monotonic id of the newest handoff — earlier in-flight calls check it after
 * their persist and stand down instead of navigating (see below).
 */
let latestHandoffId = 0;

export type HandoffNavigationResult = StashIdeaForSignupResult & {
  /** False when a newer click superseded this one — it did NOT navigate. */
  navigated: boolean;
};

/**
 * The full Build-in-Lev handoff: stash + server persist + navigation, as a
 * single-flight last-click-wins unit. Serializing only the network phases is
 * not enough — each caller navigating independently lets the FIRST click's
 * redirect unload the page while the LATEST click's persistence is still
 * queued, so the first claim would outrank the founder's final selection.
 * Here only the newest call navigates; superseded calls resolve with
 * `navigated: false` and never touch the location.
 *
 * `navigate` is injectable for tests; defaults to a hard location assign
 * (matches the previous producer behavior).
 */
export async function stashIdeaAndNavigateForSignup(opts: {
  idea: string;
  rubric: "venture" | "bootstrap";
  title?: string;
  source: OnboardingHandoffSource;
  isSignedIn: boolean;
  email?: string;
  /**
   * Also rides the destination as `from`: the completion page stamps a
   * program project only for a `from` that resolves to a program.
   */
  program?: ProgramSlug;
  navigate?: (destination: string) => void;
}): Promise<HandoffNavigationResult> {
  const callId = ++latestHandoffId;
  const { token } = await stashIdeaForSignup({
    idea: opts.idea,
    rubric: opts.rubric,
    title: opts.title,
    source: opts.source,
    program: opts.program,
  });

  if (callId !== latestHandoffId) {
    // A newer click superseded this handoff while it was persisting — its
    // navigation (with ITS idea and token) is the one that must win.
    return { token, navigated: false };
  }

  const destination = buildOnboardingHandoffDestination({
    token,
    isSignedIn: opts.isSignedIn,
    email: opts.email,
    from: opts.program,
  });
  if (opts.navigate) opts.navigate(destination);
  else window.location.href = destination;
  return { token, navigated: true };
}

/** Phase 2: best-effort server persist (mint + push), time-capped. */
async function serverPersist(
  result: OnboardingResult,
): Promise<StashIdeaForSignupResult> {
  let token: string | null = null;
  try {
    const raced = await Promise.race([
      mintAndPush(result),
      timeoutNull(SERVER_PERSIST_TIMEOUT_MS),
    ]);
    // timeoutNull resolves to null; a completed mint resolves to the outcome.
    token = raced?.token ?? null;
  } catch {
    /* expected: network error, rate limit, etc. — the local stash covers it */
  }

  // An idempotent mint (created:false — this tab minted earlier, e.g. during
  // the landing chat) returns no token, but the earlier mint's token is still
  // stashed — reuse it so the handoff URL keeps its cross-context claim. Safe
  // against the timeout race: the stash only ever holds VERIFIED tokens (see
  // mintAndPush) — a timed-out current attempt cannot have stored one, so
  // this read never exposes a token for a session whose finalize is pending.
  return { token: token ?? readStashedClaimToken() };
}

/**
 * Build a minimal OnboardingResult suitable for the server session, using the
 * same buildSkipResult + rubric pattern the pending-idea resolver uses on the
 * receiving end. Sets `bigIdea.oneLiner` from `title` when provided so project
 * names are clean.
 */
function buildOnboardingResultForHandoff(payload: {
  idea: string;
  rubric: "venture" | "bootstrap";
  title?: string;
  program?: ProgramSlug;
}): OnboardingResult {
  const result = applyProgramSeed(buildSkipResult(null, payload.idea), payload.program ?? null);
  result.rubricType = payload.rubric;
  if (payload.title) {
    result.bigIdea.oneLiner = payload.title.slice(0, 60);
  }
  return result;
}

type MintAndPushResult = {
  /** Raw claim token when the mint created a new session; null otherwise. */
  token: string | null;
  /**
   * True only when the result PATCH verifiably landed (2xx). A cross-context
   * claim AND the anonymous-project route both read the pushed session, so
   * callers that need that read to succeed key off this, not `token` (which is
   * also null for an idempotent mint whose push succeeded).
   */
  pushed: boolean;
};

/** Mint an anonymous session, then push the result onto it. */
async function mintAndPush(
  result: OnboardingResult,
): Promise<MintAndPushResult> {
  const mintRes = await fetch("/api/onboarding/session", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ posthogDistinctId: currentPosthogDistinctId() }),
  });
  if (!mintRes.ok) return { token: null, pushed: false };

  const mintBody = (await mintRes.json()) as {
    ok?: boolean;
    created?: boolean;
    token?: string;
  };
  const rawToken = mintBody.created && mintBody.token ? mintBody.token : null;

  // Push the result onto the session. Awaited AND verified: a cross-context
  // claim (the whole point of the token) reads ONLY what this PATCH
  // persisted — there is no sessionStorage fallback in the other browser, so
  // exposing a token before the push verifiably landed would hand the
  // claimant an empty session (the original lost-idea failure). keepalive
  // lets the request survive an unload if the caller's time cap fires first.
  try {
    const patchRes = await fetch("/api/onboarding/session", {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ result }),
      keepalive: true,
    });
    if (!patchRes.ok) return { token: null, pushed: false };
  } catch {
    // expected: network error — the sessionStorage stash covers conversion,
    // and no token escapes for an unverified session.
    return { token: null, pushed: false };
  }

  // Only a VERIFIED finalize stores + returns the token (Layer 4). The stash
  // therefore only ever holds tokens whose sessions carry the idea. (The chat
  // flow stores at mint time instead — safe there because the chat route
  // persists content server-side per turn.)
  if (rawToken) storeStashedClaimToken(rawToken);
  return { token: rawToken, pushed: true };
}

/** Resolves to null after `ms` — used as a timeout race partner. */
function timeoutNull(ms: number): Promise<null> {
  return new Promise((resolve) => setTimeout(() => resolve(null), ms));
}

/**
 * Persist a freshly-minted raw claim token for the redirect URL (Layer 4).
 * Written by stashIdeaForSignup (Build-in-Lev surfaces) and by the chat
 * flow's session mint (`mintSessionOnce` in useOnboardingSessionLifecycle,
 * called from IdeaChatInput) — both call this on a CREATE response.
 */
export function storeStashedClaimToken(token: string): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(SS_ONBOARDING_CLAIM_TOKEN, token);
  } catch {
    /* benign: sessionStorage unavailable */
  }
}

/**
 * Read the stashed claim token from sessionStorage. Returns null when absent.
 */
export function readStashedClaimToken(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage.getItem(SS_ONBOARDING_CLAIM_TOKEN);
  } catch {
    /* benign: sessionStorage unavailable */
    return null;
  }
}

/**
 * Clear the stashed claim token — called after a successful conversion (the
 * token is spent). NOTE: session.ts's clearOnboardingBackups also removes this
 * key directly (it cannot import from here — handoff.ts imports session.ts) so
 * the canvas handoff cleans it up too; keep the two in sync.
 */
export function clearStashedClaimToken(): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(SS_ONBOARDING_CLAIM_TOKEN);
  } catch {
    /* benign: sessionStorage unavailable, best-effort cleanup */
  }
}
